import type { StarterSource } from "./program-records";
import { z } from "zod";

const manifestSchema = z.object({ player: z.array(z.unknown()) });
const playerSchema = z.object({
  id: z.string(),
  name: z.unknown().optional(),
  description: z.unknown().optional(),
  file: z.unknown().optional(),
  source_url: z.unknown().optional(),
  image: z.unknown().optional(),
});

/** Read a public starter source from a creator-owned manifest. */
export function starterSourceOf({
  manifest,
  coworldId,
  playerId,
}: {
  readonly manifest: unknown;
  readonly coworldId: string;
  readonly playerId: string;
}): StarterSource | null {
  const parsed = manifestSchema.safeParse(manifest);

  if (!parsed.success) return null;

  const p = parsed.data.player
    .map((value) => playerSchema.safeParse(value))
    .find((value) => value.success && value.data.id === playerId);

  if (!p?.success) return null;

  const source = p.data;
  const player = typeof source.name === "string" ? source.name : playerId;
  const description =
    typeof source.description === "string" ? source.description : null;

  if (
    typeof source.file === "string" &&
    /^sha256:[0-9a-f]{64}$/.test(source.file)
  ) {
    const sha256 = source.file.slice("sha256:".length);
    return {
      player,
      description,
      kind: "file",
      sha256,
      download: `/api/observatory/v2/coworlds/${encodeURIComponent(coworldId)}/player-files/${sha256}`,
    };
  }

  if (
    typeof source.source_url === "string" &&
    source.source_url.startsWith("https://")
  )
    return {
      player,
      description,
      kind: "repo",
      href: source.source_url,
      image: typeof source.image === "string" ? source.image : null,
    };

  return null;
}
